import { useCallback } from 'react';

import type { UserConfig } from '@/entities/user-config';
import { useSaveUserConfig, useUserConfig, useUserDetails } from '@/features/_shared/user';
import isAiMessage from '@/features/ai-chat/lib/isAiMessage';
import saveUserHistory, { deriveConversationName } from '@/features/ai-chat/lib/saveHistory';
import { useChat } from '@/features/ai-chat/state/useChat';
import type { Conversation, Message } from '@/features/ai-chat/types/chat.types';
import { clock } from '@/services/clock';

export const useRateAnswer = () => {
  const { messages, setMessages, conversationId } = useChat();
  const { data: userConfigData } = useUserConfig();
  const { userEmail } = useUserDetails();
  const saveUserConfigMutation = useSaveUserConfig(userEmail ?? '');

  const handleRate = useCallback(
    (targetId: string, score: -1 | 1) => {
      const nextMsgs: Message[] = messages.map(m =>
        m.id === targetId && isAiMessage(m) ? { ...m, rating: score } : m
      );
      setMessages(nextMsgs);
      if (!conversationId || !userConfigData) return;

      const timestamp = clock.date().toISOString();
      const existing = userConfigData.chatHistory as Conversation[];
      const idx = existing.findIndex(c => c.conversationId === conversationId);
      const updatedConversation = {
        conversationId,
        messages: nextMsgs,
        timestamp,
        name: deriveConversationName(nextMsgs)
      };
      saveUserHistory({
        userConfig: userConfigData,
        saveUser: (config: UserConfig) => {
          saveUserConfigMutation.mutate(config);
        },
        updatedConversation,
        existing,
        idx
      });
    },
    [conversationId, messages, setMessages, userConfigData, saveUserConfigMutation]
  );

  return { handleRate, conversationId } as const;
};
